import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // ✅ Direct paths to images in the public folder (no imports needed)
  const CATEGORY_IMAGES = {
    'Individual': '/assets/images/retails2.jpg',
    'Corporate': '/assets/images/corporate2.jpg',
    'SME': '/assets/images/sme2.jpg',
    'Family': '/assets/images/family.jpg'
  };

  const categories = [
    {
      id: 1,
      title: "Comprehensive Health Insurance Services",
      highlight: "for Individuals",
      subtitle: "Personalized Healthcare Coverage",
      description: "Access top healthcare providers, simplify claims management, and enjoy comprehensive health solutions tailored for you and your family.",
      ctaText: "Get Started",
      ctaLink: "/get-started/individual",
      category: "Individual"
    },
    {
      id: 2,
      title: "Corporate Health Plans",
      highlight: "for Your Team",
      subtitle: "Enterprise Healthcare Solutions",
      description: "Provide your employees with world-class healthcare coverage. Streamlined administration and comprehensive benefits for organizations of all sizes.",
      ctaText: "Explore Corporate Plans",
      ctaLink: "/get-started/corporate",
      category: "Corporate"
    },
    {
      id: 3,
      title: "SME Health Coverage",
      highlight: "That Works",
      subtitle: "Small & Medium Enterprise Plans",
      description: "Affordable, flexible health insurance designed for growing businesses. Protect your team without breaking the bank.",
      ctaText: "Learn More",
      ctaLink: "/get-started/sme",
      category: "SME"
    },
    {
      id: 4,
      title: "Family Health Protection",
      highlight: "Plans",
      subtitle: "Complete Family Coverage",
      description: "Comprehensive healthcare for every member of your family. From pediatric care to senior wellness, we've got you covered.",
      ctaText: "Protect Your Family",
      ctaLink: "/get-started/family",
      category: "Family"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % categories.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [categories.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % categories.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + categories.length) % categories.length);

  const currentCategory = categories[currentSlide];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --font-primary: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
          --color-bg: #fafbfc;
          --color-text: #0f172a;
          --color-text-muted: #64748b;
          --brand-color: #e88c38;
          --brand-color-rgb: 232, 140, 56;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes imageZoomFade {
          from { opacity: 0; transform: scale(1.05); }
          to { opacity: 1; transform: scale(1); }
        }

        .medolia-hero-carousel {
          padding: 120px 0;
          background-color: var(--color-bg);
          position: relative;
          overflow: visible;
          font-family: var(--font-primary);
          min-height: 800px;
          display: flex;
          align-items: center;
        }

        .hero-bg-gradient {
          position: absolute;
          top: -20%;
          right: -10%;
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, rgba(var(--brand-color-rgb), 0.15) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(80px);
          z-index: 0;
        }

        .hero-bg-grid {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(148, 163, 184, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148, 163, 184, 0.05) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(circle at center, black 40%, transparent 100%);
          z-index: 0;
        }

        .hero-content { text-align: left; }

        .animate-stagger > * {
          opacity: 0;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-stagger .sub-title { animation-delay: 0.1s; }
        .animate-stagger h1 { animation-delay: 0.2s; }
        .animate-stagger p { animation-delay: 0.3s; }
        .animate-stagger .medolia-button-box { animation-delay: 0.4s; }
        .animate-stagger .carousel-dots { animation-delay: 0.5s; }

        .sub-title {
          color: var(--brand-color);
          font-weight: 700;
          text-transform: uppercase;
          font-size: 14px;
          letter-spacing: 2px;
          margin-bottom: 12px;
          display: inline-block;
        }

        .medolia-hero-carousel h1 {
          font-size: 52px;
          line-height: 1.15;
          font-weight: 800;
          color: var(--color-text);
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .medolia-hero-carousel h1 .highlight {
          color: var(--brand-color);
          display: block;
          margin-top: 8px;
        }

        .medolia-hero-carousel p {
          font-size: 18px;
          line-height: 1.7;
          color: var(--color-text-muted);
          margin-bottom: 40px;
          max-width: 600px;
          font-weight: 400;
        }

        .medolia-button-box {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-start;
          margin-bottom: 48px;
        }

        .theme-btn {
          padding: 16px 32px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .theme-btn.style-one {
          background-color: var(--brand-color);
          color: #ffffff;
          border: none;
          box-shadow: 0 4px 6px -1px rgba(var(--brand-color-rgb), 0.4), 0 2px 4px -1px rgba(var(--brand-color-rgb), 0.2);
        }

        .theme-btn.style-one:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(var(--brand-color-rgb), 0.4), 0 4px 6px -2px rgba(var(--brand-color-rgb), 0.2);
        }

        .theme-btn.style-two {
          background-color: transparent;
          color: var(--color-text);
          border: 1.5px solid #e2e8f0;
        }

        .theme-btn.style-two:hover {
          border-color: var(--brand-color);
          color: var(--brand-color);
          background-color: rgba(var(--brand-color-rgb), 0.05);
          transform: translateY(-2px);
        }

        .hero-image-box {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 520px;
        }

        .medolia-image {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 
            0 20px 25px -5px rgba(0, 0, 0, 0.1), 
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.5) inset;
        }

        .medolia-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          animation: imageZoomFade 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .slide-counter {
          position: absolute;
          bottom: 24px;
          right: 24px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          padding: 10px 20px;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 700;
          color: var(--color-text);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          z-index: 2;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .slide-counter .divider {
          width: 4px;
          height: 4px;
          background-color: #cbd5e1;
          border-radius: 50%;
        }

        .carousel-dots {
          display: flex;
          gap: 8px;
          align-items: center;
          justify-content: flex-start;
        }

        .dot {
          width: 40px;
          height: 4px;
          border-radius: 4px;
          background-color: #e2e8f0;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          padding: 0;
          position: relative;
          overflow: hidden;
        }

        .dot::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 0%;
          background-color: var(--brand-color);
          transition: width 0.4s ease;
          border-radius: 4px;
        }

        .dot.active {
          width: 60px;
          background-color: rgba(var(--brand-color-rgb), 0.2);
        }

        .dot.active::after { width: 100%; }
        .dot:hover { background-color: rgba(var(--brand-color-rgb), 0.3); }

        .carousel-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          border: 2px solid rgba(226, 232, 240, 0.8);
          width: 60px;
          height: 60px;
          border-radius: 50%;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: var(--color-text);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          opacity: 0.9;
        }

        .carousel-arrow:hover {
          background-color: var(--brand-color);
          color: white;
          border-color: var(--brand-color);
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 15px 30px -5px rgba(var(--brand-color-rgb), 0.4);
          opacity: 1;
        }

        .carousel-arrow.prev { left: 20px; }
        .carousel-arrow.next { right: 20px; }

        @media (max-width: 1199px) {
          .medolia-hero-carousel h1 { font-size: 48px; }
          .carousel-arrow.prev { left: 10px; }
          .carousel-arrow.next { right: 10px; }
        }

        @media (max-width: 991px) {
          .medolia-hero-carousel { padding: 80px 0; text-align: center; min-height: auto; }
          .hero-content { text-align: center; }
          .medolia-button-box { justify-content: center; }
          .carousel-dots { justify-content: center; }
          .medolia-hero-carousel h1 { font-size: 40px; }
          .medolia-hero-carousel p { margin-left: auto; margin-right: auto; }
          .hero-image-box { margin-top: 60px; height: 400px; }
          .carousel-arrow { width: 50px; height: 50px; font-size: 20px; }
          .carousel-arrow.prev { left: 5px; }
          .carousel-arrow.next { right: 5px; }
        }

        @media (max-width: 768px) {
          .medolia-hero-carousel h1 { font-size: 32px; }
          .theme-btn { padding: 14px 24px; font-size: 15px; width: 100%; justify-content: center; }
          .hero-image-box { height: 300px; }
          .carousel-arrow { display: none; }
        }
      `}</style>

      <section className="medolia-hero-carousel">
        <div className="hero-bg-gradient"></div>
        <div className="hero-bg-grid"></div>
        
        <button className="carousel-arrow prev" onClick={prevSlide} aria-label="Previous slide">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <button className="carousel-arrow next" onClick={nextSlide} aria-label="Next slide">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center align-items-center">
            <div className="col-xl-7 col-lg-6">
              <div className="hero-content animate-stagger" key={currentSlide}>
                <div className="sub-title">
                  {currentCategory.subtitle}
                </div>
                
                <h1>
                  {currentCategory.title}
                  <span className="highlight">{currentCategory.highlight}</span>
                </h1>
                
                <p>
                  {currentCategory.description}
                </p>
                
                <div className="medolia-button-box">
                  <Link to={currentCategory.ctaLink} className="theme-btn style-one">
                    {currentCategory.ctaText}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </Link>
                  <a href="/contact" className="theme-btn style-two">
                    Talk to us
                  </a>
                </div>

                <div className="carousel-dots">
                  {categories.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="col-xl-5 col-lg-6">
              <div className="hero-image-box">
                <div className="medolia-image">
                  <img 
                    key={`img-${currentSlide}`}
                    src={CATEGORY_IMAGES[currentCategory.category]} 
                    alt={currentCategory.title} 
                    onError={(e) => {
                      // Fallback to a generic placeholder if the local image fails to load
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop";
                    }} 
                  />
                  <div className="slide-counter">
                    <span>0{currentSlide + 1}</span>
                    <span className="divider"></span>
                    <span>0{categories.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Hero;